import React, { useEffect, useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { cx } from './cx';
import Skeleton from './Skeleton';
import Modal from './Modal';
import { wfGiLabelStyle, wfMutedStyle, wfCardTitleStyle } from '@/styles/typography';
import { playClickSound } from '@/utils/soundUtils';

const FALLBACK_ICON = '/icons/egame.jpg';

export interface GameTileProps {
  name: string;
  imageUrl?: string | null;
  hot?: boolean;
  balance?: number | null;
  footer?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  className?: string;
  /** Skip automatic click sound when onClick is set. */
  silent?: boolean;
  /** Gray icon + label overlaid on the art (Coming soon). */
  comingSoon?: boolean;
  comingSoonLabel?: string;
  /** Bật hiệu ứng sóng (tidal wave) quét chéo; hàng của tile trong grid */
  waveRow?: number;
  /** Cột của tile trong grid — delay = (waveRow + waveCol) × stagger */
  waveCol?: number;
  /**
   * Periodic diagonal mirror shine (Home browse grid). Same gleam as `.btn-shine`,
   * phase-offset by (shineRow + shineCol) so one wavefront sweeps the matrix.
   */
  shineRow?: number;
  shineCol?: number;
  /**
   * Clone diện mạo card từ Games page: khung `.wf-games-page-card` (viền + shadow +
   * hairline + highlight), tooltip tên, nút zoom xem ảnh full-size. Mặc định tắt để
   * không đổi giao diện các nơi dùng GameTile khác (vd DepositPage picker).
   */
  card?: boolean;
}

function NebulaArt({ name, className }: { name: string; className?: string }) {
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className={cx('w-full h-full bg-[linear-gradient(160deg,#1D3FA8_0%,#0A1B52_100%)] flex items-center justify-center', className)}>
      <span
        className="font-display font-bold text-white/80 tracking-wider"
        style={{ fontSize: 'var(--type-header2)' }}
      >
        {initials}
      </span>
    </div>
  );
}

/** Icon art clone từ `GameCardArt` (GamesPage): fade-in khi load xong, fallback ảnh khi lỗi. */
function CardArt({ imageUrl, name }: { imageUrl: string; name: string }) {
  const [ready, setReady] = useState(false);
  const [src, setSrc] = useState(imageUrl);
  useEffect(() => {
    setReady(false);
    setSrc(imageUrl);
  }, [imageUrl]);
  return (
    <>
      {!ready && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
      <img
        src={src}
        alt={name}
        draggable={false}
        loading="eager"
        decoding="async"
        className={cx(
          'absolute inset-0 block h-full w-full object-cover pointer-events-none',
          !ready && 'opacity-0'
        )}
        onLoad={() => setReady(true)}
        onError={() => {
          if (src !== FALLBACK_ICON) {
            setSrc(FALLBACK_ICON);
            return;
          }
          setReady(true);
        }}
      />
    </>
  );
}

/** Wireframe `.gi` — icon + label at fixed --gi size. */
export default function GameTile({
  name,
  imageUrl,
  hot,
  balance,
  footer,
  onClick,
  className,
  comingSoon,
  comingSoonLabel = 'Coming soon',
  waveRow,
  waveCol = 0,
  shineRow,
  shineCol = 0,
  card,
  silent = false,
}: GameTileProps) {
  const wave = waveRow != null;
  const shine = shineRow != null;
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(imageUrl || FALLBACK_ICON);
  const [zoomOpen, setZoomOpen] = useState(false);
  const showHot = hot && !comingSoon;

  useEffect(() => {
    if (!imageUrl) return;
    setImgLoaded(false);
    setImgSrc(imageUrl);
  }, [imageUrl]);

  const handleActivate: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!onClick) return;
    if (!silent) playClickSound();
    onClick(e);
  };

  if (card) {
    return (
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={handleActivate}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActivate(e as unknown as React.MouseEvent<HTMLDivElement>);
                }
              }
            : undefined
        }
        className={cx(
          'wf-games-page-card relative flex flex-col items-center min-w-0',
          onClick && 'cursor-pointer focus-visible:outline-none focus-visible:shadow-focus-ring',
          wave && 'game-tile-wave',
          comingSoon && 'game-tile--coming-soon',
          className
        )}
        style={{
          gap: 'var(--proto-chip-gap)',
          ...(wave
            ? ({
                '--wave-row': waveRow,
                '--wave-col': waveCol,
              } as React.CSSProperties)
            : undefined),
        }}
      >
        <div className="wf-games-page-card__lift w-full">
          <div className="wf-games-page-card__face">
            <div className="wf-games-page-card__head">
              <p
                className="wf-games-page-tip font-ui font-bold text-ink truncate"
                style={wfCardTitleStyle}
                data-tooltip={name}
              >
                {name}
              </p>
            </div>
            <div className="wf-games-page-card__media">
              {imageUrl ? <CardArt imageUrl={imageUrl} name={name} /> : <NebulaArt name={name} className="absolute inset-0" />}
              {comingSoon ? (
                <span className="game-tile-coming-soon-overlay" aria-hidden>
                  {comingSoonLabel}
                </span>
              ) : null}
              {showHot && (
                <span
                  className="absolute top-1 right-1 z-[4] px-1.5 rounded-full bg-gradient-to-b from-danger to-danger-deep text-white font-ui font-bold uppercase tracking-[0.1em] inline-flex items-center"
                  style={{ fontSize: 'var(--fs-base)', height: 'calc(14px * var(--proto-scale))' }}
                >
                  Hot
                </span>
              )}
              {imageUrl && !comingSoon && (
                <button
                  type="button"
                  className="wf-games-page-card__zoom"
                  aria-label="Enlarge image"
                  title="Enlarge image"
                  style={{ top: 'auto', left: 'auto', right: 10, bottom: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setZoomOpen(true);
                  }}
                >
                  <ZoomIn className="h-3.5 w-3.5" strokeWidth={2.25} />
                </button>
              )}
            </div>
          </div>
        </div>
        {typeof balance === 'number' && (
          <p
            className="font-money text-money-out-soft tabular-nums min-w-0 w-full max-w-[var(--gi)] truncate text-center"
            style={wfMutedStyle}
          >
            $
            {balance.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        )}
        {footer ? <div className="min-w-0 w-full text-center">{footer}</div> : null}
        {imageUrl && (
          <Modal open={zoomOpen} onClose={() => setZoomOpen(false)} title={name} size="lg">
            <div className="wf-games-page-art-preview">
              <img
                src={imageUrl}
                alt={name}
                className="wf-games-page-art-preview__img"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = FALLBACK_ICON;
                }}
              />
            </div>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={handleActivate}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivate(e as unknown as React.MouseEvent<HTMLDivElement>);
              }
            }
          : undefined
      }
      className={cx(
        'relative flex flex-col items-center shrink-0 min-w-0',
        'w-[var(--gi)] max-w-[var(--gi)]',
        onClick && 'cursor-pointer focus-visible:outline-none focus-visible:shadow-focus-ring',
        wave && 'game-tile-wave',
        shine && 'game-tile-shine',
        comingSoon && 'game-tile--coming-soon',
        className
      )}
      style={{
        gap: 'var(--proto-chip-gap)',
        ...(wave
          ? ({
              '--wave-row': waveRow,
              '--wave-col': waveCol,
            } as React.CSSProperties)
          : undefined),
        ...(shine
          ? ({
              '--shine-row': shineRow,
              '--shine-col': shineCol,
            } as React.CSSProperties)
          : undefined),
      }}
    >
      <div className="lobby-gi-ico relative h-[var(--gi)] w-[var(--gi)] shrink-0 overflow-hidden">
        {imageUrl ? (
          <>
            {!imgLoaded && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
            <img
              ref={(el) => {
                if (el?.complete && el.naturalWidth > 0) setImgLoaded(true);
              }}
              src={imgSrc}
              alt={name}
              loading="eager"
              decoding="async"
              draggable={false}
              onLoad={() => setImgLoaded(true)}
              onError={() => {
                if (imgSrc !== FALLBACK_ICON) {
                  setImgSrc(FALLBACK_ICON);
                  return;
                }
                setImgLoaded(true);
              }}
              className={cx(
                'block w-full h-full object-cover pointer-events-none select-none',
                !imgLoaded && 'opacity-0'
              )}
            />
          </>
        ) : (
          <NebulaArt name={name} />
        )}
        {comingSoon ? (
          <span className="game-tile-coming-soon-overlay" aria-hidden>
            {comingSoonLabel}
          </span>
        ) : null}
        {showHot && (
          <span
            className="absolute top-1 right-1 z-[2] px-1.5 rounded-full bg-gradient-to-b from-danger to-danger-deep text-white font-ui font-bold uppercase tracking-[0.1em] inline-flex items-center"
            style={{ fontSize: 'var(--fs-base)', height: 'calc(14px * var(--proto-scale))' }}
          >
            Hot
          </span>
        )}
      </div>
      <p
        className="font-ui font-bold leading-snug text-ink text-center min-w-0 w-full max-w-[var(--gi)] truncate px-0.5"
        style={wfGiLabelStyle}
        title={name}
      >
        {name}
      </p>
      {typeof balance === 'number' && (
        <p
          className="font-money text-money-out-soft tabular-nums min-w-0 w-full max-w-[var(--gi)] truncate text-center"
          style={wfMutedStyle}
        >
          $
          {balance.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </p>
      )}
      {footer ? (
        <div className="min-w-0 w-full text-center">{footer}</div>
      ) : null}
    </div>
  );
}
